import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import MathText from '../../components/MathText'; 

const TeacherDashboard = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    // === БЕЗОПАСНОЕ ИЗВЛЕЧЕНИЕ ПАРАМЕТРОВ ===
    const tabParam = searchParams.get('tab');
    const groupIdParam = searchParams.get('groupId');
    const templateIdParam = searchParams.get('templateId');
    const actionParam = searchParams.get('action');

    // === ГЛОБАЛЬНАЯ НАВИГАЦИЯ ===
    const [leftTab, setLeftTab] = useState(tabParam || 'classes'); 

    // === СТЕЙТЫ ДЛЯ ГРУПП (КЛАССОВ) ===
    const [groups, setGroups] = useState([]);
    const [students, setStudents] = useState([]);
    const [tests, setTests] = useState([]);
    const [activeGroup, setActiveGroup] = useState(groupIdParam ? parseInt(groupIdParam) : null);
    const [activeTab, setActiveTab] = useState('tests');

    // === СТЕЙТЫ ДЛЯ ШАБЛОНОВ ===
    const [templates, setTemplates] = useState([]); 
    const [activeTemplate, setActiveTemplate] = useState(templateIdParam ? parseInt(templateIdParam) : null);
    const [activeTemplateData, setActiveTemplateData] = useState(null);
    const [templateLoading, setTemplateLoading] = useState(false);

    // === СТЕЙТЫ ДЛЯ ОБЩИХ ЗАГРУЗОК И МОДАЛОК ===
    const [loading, setLoading] = useState(true);
    const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [inviteUsername, setInviteUsername] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    
    const [isAssignTestModalOpen, setIsAssignTestModalOpen] = useState(false);
    const [testForm, setTestForm] = useState({ 
        template_id: '', 
        start_time: '', 
        end_time: '', 
        assign_type: 'group', 
        selected_students: [] 
    });

    // === СТЕЙТЫ ДЛЯ ЧАТА ===
    const [groupChat, setGroupChat] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [newChatMsg, setNewChatMsg] = useState('');
    const messagesEndRef = useRef(null);

    // === СТЕЙТЫ ДЛЯ АНАЛИТИКИ И ПРЕДПРОСМОТРА ===
    const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
    const [analyticsData, setAnalyticsData] = useState([]);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [currentTestName, setCurrentTestName] = useState('');

    const [isQuestionsPreviewOpen, setIsQuestionsPreviewOpen] = useState(false);
    const [previewQuestions, setPreviewQuestions] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);

    // === СИНХРОНИЗАЦИЯ С URL ===
    useEffect(() => {
        if (tabParam) setLeftTab(tabParam);
        if (groupIdParam) {
            setActiveGroup(parseInt(groupIdParam));
            setLeftTab('classes');
        }
        if (templateIdParam) {
            setActiveTemplate(parseInt(templateIdParam));
            setLeftTab('templates');
        }
        if (actionParam === 'new' || actionParam === 'new-group') {
            setIsCreateGroupModalOpen(true);
        }
    }, [tabParam, groupIdParam, templateIdParam, actionParam]);

    const closeCreateGroupModal = () => {
        setIsCreateGroupModalOpen(false);
        if (searchParams.has('action')) {
            searchParams.delete('action');
            setSearchParams(searchParams);
        }
        setNewGroupName('');
    };

    // 1. ЗАГРУЗКА ГРУПП
    const fetchGroups = useCallback(async () => {
        try {
            const res = await API.get('/api/teacher/groups/');
            setGroups(res.data);
            if (res.data.length > 0 && !activeGroup && !groupIdParam) {
                setActiveGroup(res.data[0].id);
            }
        } catch (error) {
            console.error("Ошибка загрузки групп", error);
        } finally { 
            setLoading(false); 
        }
    }, [activeGroup, groupIdParam]);

    useEffect(() => { 
        fetchGroups(); 
    }, [fetchGroups]);

    // 2. ЗАГРУЗКА ШАБЛОНОВ
    const fetchTemplates = useCallback(async () => {
        try {
            const res = await API.get('/api/test-templates/');
            setTemplates(res.data);
        } catch (err) {
            console.error("Ошибка загрузки шаблонов:", err);
        }
    }, []);

    useEffect(() => { 
        fetchTemplates(); 
    }, [fetchTemplates]);

    // 3. АВТОВЫБОР ПЕРВОГО ШАБЛОНА
    useEffect(() => {
        if (leftTab === 'templates' && templates.length > 0 && !activeTemplate && !templateIdParam) {
            setActiveTemplate(templates[0].id);
        }
    }, [leftTab, templates, activeTemplate, templateIdParam]);

    // 4. ЗАГРУЗКА ПОДРОБНОСТЕЙ ВЫБРАННОГО ШАБЛОНА
    useEffect(() => {
        if (leftTab === 'templates' && activeTemplate) {
            setTemplateLoading(true);
            API.get(`/api/test-templates/${activeTemplate}/`)
                .then(res => setActiveTemplateData(res.data))
                .catch(err => console.error(err))
                .finally(() => setTemplateLoading(false));
        }
    }, [activeTemplate, leftTab]);

    // 5. ЗАГРУЗКА ДАННЫХ ГРУППЫ
    const fetchGroupData = useCallback(async () => {
        if (!activeGroup) return;
        try {
            const [studentsRes, testsRes, chatsRes] = await Promise.all([
                API.get(`/api/teacher/groups/${activeGroup}/students/`),
                API.get(`/api/teacher/groups/${activeGroup}/tests/`),
                API.get('/api/chats/')
            ]);
            setStudents(studentsRes.data);
            setTests(testsRes.data);
            
            const chat = chatsRes.data.find(c => 
                (c.type === 'group' || c.room_type === 'group') && 
                (c.group_id === activeGroup || c.group === activeGroup)
            );
            if (chat) {
                setGroupChat(chat);
                const msgsRes = await API.get(`/api/chats/${chat.id}/messages/`);
                setChatMessages(msgsRes.data);
            }
        } catch (error) { 
            console.error(error); 
        }
    }, [activeGroup]);

    useEffect(() => { 
        if (leftTab === 'classes') fetchGroupData(); 
    }, [fetchGroupData, leftTab]);

    // ОБНОВЛЕНИЕ ЧАТА
    useEffect(() => {
        if (leftTab === 'classes' && activeTab === 'chat' && groupChat) {
            const interval = setInterval(async () => {
                try {
                    const res = await API.get(`/api/chats/${groupChat.id}/messages/`);
                    setChatMessages(res.data);
                } catch (error) {}
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [activeTab, groupChat, leftTab]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [chatMessages, activeTab]);

    // ПОИСК УЧЕНИКА
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (inviteUsername.trim().length >= 2) {
                setIsSearching(true);
                try {
                    const response = await API.get(`/api/users/search/?q=${inviteUsername}`);
                    setSearchResults(response.data);
                } catch (error) {
                    setSearchResults([]);
                } finally {
                    setIsSearching(false);
                }
            } else {
                setSearchResults([]); 
            }
        }, 300); 
        return () => clearTimeout(delayDebounceFn);
    }, [inviteUsername]);

    // === ОБРАБОТЧИКИ СОБЫТИЙ (ГРУППЫ) ===
    const handleCreateGroup = async (e) => {
        e.preventDefault(); 
        if (!newGroupName.trim()) return;
        try {
            const res = await API.post('/api/teacher/groups/', { name: newGroupName });
            setGroups([res.data, ...groups]); 
            setActiveGroup(res.data.id);
            closeCreateGroupModal(); 
        } catch (error) { 
            alert("Не удалось создать группу"); 
        }
    };

    const handleInviteStudent = async (e) => {
        e.preventDefault(); 
        if (!inviteUsername.trim() || !activeGroup) return;
        try {
            await API.post(`/api/teacher/groups/${activeGroup}/add-student/`, { username: inviteUsername });
            setInviteUsername(''); 
            setSearchResults([]); 
            setIsInviteModalOpen(false);
            fetchGroupData(); 
            fetchGroups(); 
            alert("Ученик добавлен!");
        } catch (error) { 
            alert(error.response?.data?.error || "Ошибка"); 
        }
    };

    const handleRemoveStudent = async (studentId, studentName) => {
        if (!window.confirm(`Исключить ученика ${studentName}?`)) return;
        try { 
            await API.post(`/api/teacher/groups/${activeGroup}/remove-student/${studentId}/`); 
            fetchGroupData(); 
            fetchGroups(); 
        } catch (error) { 
            alert("Ошибка исключения"); 
        }
    };

    const handleAssignTest = async (e) => {
        e.preventDefault(); 
        if (!activeGroup || !testForm.template_id) return;
        const selectedTemplate = templates.find(t => t.id === parseInt(testForm.template_id));
        try {
            await API.post(`/api/teacher/groups/${activeGroup}/tests/`, {
                ...testForm,
                title: selectedTemplate ? selectedTemplate.title : 'Без названия',
                template: testForm.template_id 
            });
            setIsAssignTestModalOpen(false);
            setTestForm({ template_id: '', start_time: '', end_time: '', assign_type: 'group', selected_students: [] });
            fetchGroupData();
            alert("Тест успешно опубликован для группы!");
        } catch (error) { 
            alert("Ошибка при публикации теста"); 
        }
    };

    const handleDeleteTest = async (testId) => {
        if (!window.confirm("Удалить этот тест из расписания?")) return;
        try { 
            await API.delete(`/api/teacher/groups/${activeGroup}/tests/${testId}/`); 
            fetchGroupData(); 
        } catch (error) { 
            alert("Ошибка удаления"); 
        }
    };

    const sendGroupMessage = async (e) => {
        e.preventDefault(); 
        if (!newChatMsg.trim() || !groupChat) return;
        try {
            await API.post(`/api/chats/${groupChat.id}/messages/`, { text: newChatMsg });
            setNewChatMsg('');
            const res = await API.get(`/api/chats/${groupChat.id}/messages/`);
            setChatMessages(res.data);
        } catch (error) { 
            alert("Ошибка отправки"); 
        }
    };

    const startPrivateChat = async (studentId) => {
        try { 
            await API.post(`/api/chats/private/${studentId}/`); 
            navigate('/messages'); 
        } catch (error) { 
            alert("Ошибка создания личного чата"); 
        }
    };

    // === ОБРАБОТЧИК СОБЫТИЙ (ШАБЛОНЫ) ===
    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm("Вы уверены, что хотите удалить этот шаблон? Все активные тесты, привязанные к нему, могут перестать работать.")) return;
        try {
            await API.delete(`/api/test-templates/${templateId}/`);
            fetchTemplates(); 
            if (activeTemplate === templateId) setActiveTemplate(null);
            alert("Шаблон успешно удален.");
        } catch (error) { 
            alert("Ошибка удаления шаблона"); 
        }
    };
    
    // === ФУНКЦИИ ПРОСМОТРА И АНАЛИТИКИ ===
    const openAnalyticsModal = async (eventId, eventTitle) => {
        setCurrentTestName(eventTitle); 
        setIsAnalyticsModalOpen(true); 
        setAnalyticsLoading(true);
        try {
            const res = await API.get(`/api/teacher/events/${eventId}/analytics/`);
            setAnalyticsData(res.data);
        } catch (error) { 
            alert("Не удалось загрузить результаты."); 
        } finally { 
            setAnalyticsLoading(false); 
        }
    };

    const openQuestionsPreview = async (templateId, eventTitle) => {
        setCurrentTestName(eventTitle); 
        setIsQuestionsPreviewOpen(true); 
        setPreviewLoading(true);
        try {
            const res = await API.get(`/api/test-templates/${templateId}/`);
            setPreviewQuestions(res.data.questions_detail || res.data.questions || []); 
        } catch (error) { 
            alert("Не удалось загрузить вопросы. Возможно шаблон удален."); 
        } finally { 
            setPreviewLoading(false); 
        }
    };

    // === ВСПОМОГАТЕЛЬНЫЕ ДАННЫЕ ДЛЯ РЕНДЕРА ===
    const currentGroup = groups.find(g => g.id === activeGroup);
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const testDays = tests.map(t => new Date(t.end_time).getDate());

    // ==============================================================
    // РЕНДЕР: ВКЛАДКА "КЛАССЫ"
    // ==============================================================
    const renderClassesTab = () => {
        if (!currentGroup) {
            return (
                <div className="bg-white p-12 rounded-2xl border border-gray-200 text-center shadow-sm h-full flex flex-col items-center justify-center min-h-[60vh]">
                    <span className="text-5xl block mb-4">🎓</span>
                    <h2 className="text-xl font-black text-gray-800">Выберите или создайте группу в боковом меню</h2>
                    <button onClick={() => setIsCreateGroupModalOpen(true)} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-colors">
                        + Создать новую группу
                    </button>
                </div>
            );
        }

        return (
            <>
                {/* === МОБИЛЬНЫЙ СЕЛЕКТОР ГРУПП (Только для телефонов) === */}
                <div className="block lg:hidden mb-4 relative z-10">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5 block ml-1">Текущий класс:</label>
                    <div className="relative">
                        <select
                            className="w-full bg-white border border-gray-200 rounded-xl pl-4 pr-10 py-3.5 text-sm font-black text-blue-700 shadow-sm outline-none appearance-none focus:border-blue-500 transition-colors"
                            value={activeGroup || ''}
                            onChange={(e) => setActiveGroup(parseInt(e.target.value))}
                        >
                            {groups.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-blue-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-4 sm:p-6 rounded-3xl border border-gray-200 shadow-sm mb-4">
                    <div className="flex justify-between items-end mb-6">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-black text-gray-900 uppercase tracking-tight">{currentGroup.name}</h1>
                            <p className="text-xs text-gray-500 font-bold mt-1">Управление обучением группы</p>
                        </div>
                        <div className="text-right flex flex-col items-end">
                            <span className="text-2xl sm:text-3xl font-black text-blue-600 leading-none">{currentGroup.students}</span>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">Учеников</span>
                        </div>
                    </div>

                    {/* Вкладки навигации по группе (со скрытым системным скроллбаром) */}
                    <div className="flex gap-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-100 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                        {[
                            { id: 'students', icon: '👨‍🎓', label: 'Ученики' },
                            { id: 'tests', icon: '📝', label: 'Тесты и Дедлайны' },
                            { id: 'chat', icon: '💬', label: 'Общий чат' },
                            { id: 'rating', icon: '🏆', label: 'Рейтинг' }
                        ].map(tab => (
                            <button 
                                key={tab.id} 
                                onClick={() => setActiveTab(tab.id)} 
                                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-white text-blue-600 shadow-md border border-gray-200' : 'text-gray-500 hover:bg-gray-100'}`}
                            >
                                <span className="text-base">{tab.icon}</span> {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-200 shadow-sm min-h-[500px] flex flex-col overflow-hidden">
                        
                        {/* ВКЛАДКА: СТУДЕНТЫ */}
                        {activeTab === 'students' && (
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-base font-black text-gray-800">Список учеников</h2>
                                    <button onClick={() => setIsInviteModalOpen(true)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-black px-4 py-2.5 rounded-xl transition-colors">
                                        + ПРИГЛАСИТЬ
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {students.length === 0 && <p className="text-center text-gray-400 text-xs font-bold py-10 border-2 border-dashed rounded-2xl">В группе нет учеников.</p>}
                                    {students.map(student => (
                                        <div key={student.id} className="flex items-center justify-between p-4 rounded-2xl border bg-white border-gray-100 hover:shadow-md transition-shadow">
                                            <div className="flex items-center gap-4">
                                                <div className="relative">
                                                    <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-xl shadow-inner border border-blue-100">👦</div>
                                                    {student.isOnline && <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></span>}
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-bold text-gray-800">{student.name}</h3>
                                                    <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest mt-1">Ср. балл: {student.avgScore}%</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => startPrivateChat(student.id)} className="bg-blue-50 text-blue-600 hover:bg-blue-100 text-[11px] font-bold px-4 py-2 rounded-xl transition-colors">✉️ ЛС</button>
                                                <button onClick={() => handleRemoveStudent(student.id, student.name)} className="text-[11px] font-bold px-4 py-2 rounded-xl border bg-red-50 text-red-600 border-red-100 hover:bg-red-100 transition-colors">Исключить</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ВКЛАДКА: ТЕСТЫ */}
                        {activeTab === 'tests' && (
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                                    <h2 className="text-base font-black text-gray-800">Назначенные тесты</h2>
                                    <button onClick={() => { setTestForm({ template_id: '', start_time: '', end_time: '', assign_type: 'group', selected_students: [] }); setIsAssignTestModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-black px-5 py-2.5 rounded-xl shadow-md transition-colors">
                                        + ПУБЛИКОВАТЬ ТЕСТ
                                    </button>
                                </div>
                                
                                <div className="space-y-4">
                                    {tests.length === 0 && <p className="text-center text-gray-400 text-xs font-bold py-12 border-2 border-dashed rounded-2xl">Группе еще не заданы тесты.</p>}
                                    {tests.map(test => {
                                        const isExpired = new Date(test.end_time) < new Date();
                                        return (
                                            <div key={test.id} className="p-5 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow relative">
                                                <div className="flex justify-between items-start mb-3 pr-6">
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1.5">
                                                            <h3 className="font-black text-base text-gray-800">{test.title}</h3>
                                                            <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-widest ${isExpired ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                                                                {isExpired ? 'Завершен' : 'Активен'}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">
                                                            Назначено: {test.assign_type === 'group' ? 'Всей группе' : 'Индивидуально'}
                                                        </p>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex flex-wrap items-center gap-4 text-xs font-black text-gray-500 bg-gray-50 p-3 rounded-xl mb-4 border border-gray-100">
                                                    <span>📅 Старт: {new Date(test.start_time).toLocaleString('ru').slice(0, 17)}</span>
                                                    <span className={isExpired ? 'text-gray-500' : 'text-red-500'}>
                                                        🚨 Конец: {new Date(test.end_time).toLocaleString('ru').slice(0, 17)}
                                                    </span>
                                                </div>
                                                
                                                <div className="flex gap-2 items-center border-t border-gray-100 pt-4">
                                                    <button onClick={() => openQuestionsPreview(test.quiz_id, test.title)} className="bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs font-bold px-4 py-2 rounded-xl transition-colors flex items-center gap-2">
                                                        👁️ Вопросы
                                                    </button>
                                                    <button onClick={() => openAnalyticsModal(test.id, test.title)} className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-bold px-4 py-2 rounded-xl transition-colors flex items-center gap-2 ml-auto">
                                                        📊 Результаты
                                                    </button>
                                                    <button onClick={() => handleDeleteTest(test.id)} className="bg-red-50 text-red-600 hover:bg-red-100 text-xs font-bold px-4 py-2 rounded-xl ml-2 transition-colors">
                                                        ✕
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {/* ВКЛАДКА: ЧАТ */}
                        {activeTab === 'chat' && (
                            <div className="h-full flex flex-col flex-1 bg-slate-50/50">
                                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                    {!groupChat && <div className="text-center text-xs font-bold text-gray-400 my-auto pt-10">Чат загружается...</div>}
                                    {chatMessages.length === 0 && groupChat && <div className="text-center text-xs font-bold text-gray-400 my-auto pt-10">Напишите первое сообщение в группу!</div>}
                                    
                                    {chatMessages.map((msg, idx) => {
                                        const senderName = msg.sender_name || msg.sender_username || msg.sender || 'Пользователь';
                                        const isMine = senderName === userData?.username || msg.sender === userData?.id;
                                        
                                        const prevMsg = chatMessages[idx - 1];
                                        const prevSenderName = prevMsg ? (prevMsg.sender_name || prevMsg.sender_username || prevMsg.sender) : null;
                                        const showName = idx === 0 || prevSenderName !== senderName;

                                        return (
                                            <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`max-w-[85%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
                                                    {showName && (
                                                        <span className="text-[10px] font-bold text-gray-500 mx-1 mb-1">
                                                            {isMine ? `Вы (${senderName})` : senderName}
                                                        </span>
                                                    )}
                                                    <div className={`px-4 py-2.5 rounded-2xl shadow-sm text-sm font-medium ${isMine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm'}`}>
                                                        {msg.text}
                                                    </div>
                                                    <span className="text-[9px] font-semibold text-gray-400 mt-1 mx-1">
                                                        {new Date(msg.created_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={messagesEndRef} />
                                </div>
                                
                                <div className="p-4 bg-white border-t border-gray-200 mt-auto">
                                    <form onSubmit={sendGroupMessage} className="flex gap-2">
                                        <input type="text" placeholder="Написать сообщение в группу..." className="flex-1 bg-gray-100 border-none rounded-xl px-4 py-3 text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500/20" value={newChatMsg} onChange={e => setNewChatMsg(e.target.value)} />
                                        <button type="submit" disabled={!newChatMsg.trim()} className="bg-blue-600 disabled:bg-blue-300 hover:bg-blue-700 text-white rounded-xl px-5 py-3 text-sm font-bold transition-colors shadow-sm">➤</button>
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* ВКЛАДКА: РЕЙТИНГ */}
                        {activeTab === 'rating' && (
                            <div className="p-6">
                                <h2 className="text-base font-black text-gray-800 mb-6">Лидерборд группы</h2>
                                <div className="space-y-2">
                                    {[...students].sort((a,b) => b.avgScore - a.avgScore).map((student, index) => (
                                        <div key={student.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 hover:shadow-sm transition-shadow">
                                            <div className="flex items-center gap-4">
                                                <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-black shadow-sm ${index === 0 ? 'bg-yellow-100 text-yellow-600' : index === 1 ? 'bg-gray-200 text-gray-600' : index === 2 ? 'bg-orange-100 text-orange-600' : 'bg-white text-gray-400 border border-gray-200'}`}>
                                                    {index + 1}
                                                </span>
                                                <span className="text-sm font-bold text-gray-800">{student.name}</span>
                                            </div>
                                            <span className="text-sm font-black text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">{student.avgScore} баллов</span>
                                        </div>
                                    ))}
                                    {students.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Нет данных для рейтинга</p>}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* МИНИ КАЛЕНДАРЬ (Скрыт на телефонах: hidden lg:block) */}
                    <div className="hidden lg:block bg-white rounded-3xl border border-gray-200 shadow-sm p-6 h-fit sticky top-4">
                        <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest mb-6 flex items-center gap-2">
                            <span>📅</span> Расписание
                        </h3>
                        <div className="grid grid-cols-7 gap-1 text-center mb-3">
                            {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(d => <div key={d} className="text-[10px] font-black text-gray-400">{d}</div>)}
                        </div>
                        <div className="grid grid-cols-7 gap-1.5 text-center">
                            {Array.from({ length: daysInMonth }).map((_, i) => {
                                const day = i + 1;
                                const hasTest = testDays.includes(day);
                                return (
                                    <div key={day} className={`aspect-square flex items-center justify-center rounded-xl text-xs font-bold transition-all cursor-default ${hasTest ? 'bg-red-100 text-red-600 border border-red-200 shadow-sm' : day === today.getDate() ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}>
                                        {day}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-6 pt-5 border-t border-gray-100 flex flex-col gap-3 text-xs font-bold text-gray-500">
                            <div className="flex items-center gap-3"><span className="w-3.5 h-3.5 rounded-md bg-red-100 border border-red-200 block"></span> — Дедлайн теста</div>
                            <div className="flex items-center gap-3"><span className="w-3.5 h-3.5 rounded-md bg-blue-600 block shadow-sm"></span> — Сегодня</div>
                        </div>
                        <button onClick={() => navigate('/calendar')} className="w-full mt-6 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-black py-3 rounded-xl transition-colors border border-gray-200">
                            ОТКРЫТЬ ПОЛНЫЙ КАЛЕНДАРЬ →
                        </button>
                    </div>
                </div>
            </>
        );
    };

    // ==============================================================
    // РЕНДЕР: ВКЛАДКА "ШАБЛОНЫ"
    // ==============================================================
    const renderTemplatesTab = () => {
        if (!activeTemplate || !activeTemplateData) {
            return (
                <div className="bg-white p-12 rounded-3xl border border-gray-200 text-center shadow-sm h-full flex flex-col items-center justify-center min-h-[60vh]">
                    <span className="text-5xl block mb-4">📁</span>
                    <h2 className="text-xl font-black text-gray-800">Выберите шаблон в боковом меню для предпросмотра</h2>
                </div>
            );
        }

        return (
            <div className="flex flex-col h-full">
                {/* === МОБИЛЬНЫЙ СЕЛЕКТОР ШАБЛОНОВ (Только для телефонов) === */}
                <div className="block lg:hidden mb-4 relative z-10">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5 block ml-1">Текущий шаблон:</label>
                    <div className="relative">
                        <select
                            className="w-full bg-white border border-gray-200 rounded-xl pl-4 pr-10 py-3.5 text-sm font-black text-blue-700 shadow-sm outline-none appearance-none focus:border-blue-500 transition-colors"
                            value={activeTemplate || ''}
                            onChange={(e) => setActiveTemplate(parseInt(e.target.value))}
                        >
                            {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.title}</option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-blue-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-3xl border border-gray-200 shadow-sm flex flex-col h-full min-h-[70vh] overflow-hidden">
                    <div className="p-8 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gray-50/50">
                        <div>
                            <h1 className="text-2xl font-black text-gray-900 mb-1.5">{activeTemplateData.title}</h1>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                                Шаблон теста • Вопросов: {activeTemplateData.questions_detail?.length || 0}
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => navigate(`/teacher/constructor?templateId=${activeTemplateData.id}`)} className="bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200 text-xs font-black px-5 py-3 rounded-xl transition-colors shadow-sm">
                                ✏️ ИЗМЕНИТЬ
                            </button>
                            <button onClick={() => handleDeleteTemplate(activeTemplateData.id)} className="bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 text-xs font-black px-5 py-3 rounded-xl transition-colors shadow-sm">
                                ✕ УДАЛИТЬ
                            </button>
                        </div>
                    </div>
                    <div className="p-8 flex-1 overflow-y-auto space-y-6 bg-gray-50">
                        {templateLoading ? (
                            <div className="text-center text-sm font-bold text-gray-400 py-12">Загрузка вопросов...</div>
                        ) : activeTemplateData.questions_detail?.length === 0 ? (
                            <div className="text-center text-sm font-bold text-gray-400 py-12 border-2 border-dashed rounded-2xl border-gray-200">В этом шаблоне пока нет вопросов.</div>
                        ) : (
                            activeTemplateData.questions_detail?.map((q, i) => (
                                <div key={q.id || i} className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm transition-all hover:shadow-md">
                                    <div className="font-bold text-sm text-gray-800 mb-5 flex gap-3 leading-relaxed">
                                        <span className="text-purple-600 font-black shrink-0">{i + 1}.</span> 
                                        <div><MathText text={q.text || 'Текст вопроса недоступен'} /></div>
                                    </div>
                                    {q.image && <img src={q.image} alt="Вопрос" className="max-h-48 object-contain mb-5 rounded-xl border border-gray-200 bg-gray-50 p-2" />}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-7 border-l-2 border-purple-100">
                                        {q.answers?.map((ans, idx) => (
                                            <div key={idx} className={`text-xs font-semibold flex items-center gap-3 p-3 rounded-xl border transition-colors ${ans.is_correct ? 'bg-green-50 text-green-800 border-green-200' : 'bg-gray-50 text-gray-600 border-transparent hover:border-gray-200'}`}>
                                                <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 font-black ${ans.is_correct ? 'bg-green-600 text-white shadow-sm' : 'bg-gray-200 text-gray-500'}`}>
                                                    {['А', 'Б', 'В', 'Г', 'Д'][idx] + ')'}
                                                </span>
                                                <div className="truncate pr-2"><MathText text={ans.text} /></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================================
    // РЕНДЕР: МОДАЛКИ
    // ==============================================================
    const renderModals = () => {
        return (
            <>
                {/* Публикация теста */}
                {isAssignTestModalOpen && (
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <form onSubmit={handleAssignTest} className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl relative">
                            <button type="button" onClick={() => setIsAssignTestModalOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-800 font-bold text-lg">✕</button>
                            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2"><span>📝</span> Публикация теста</h2>

                            <div className="space-y-3 mb-6">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Выберите шаблон теста</label>
                                    {templates.length === 0 ? (
                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-700 font-bold mb-2">
                                            У вас нет готовых шаблонов. Сначала создайте шаблон (вкладка "Мои шаблоны").
                                        </div>
                                    ) : (
                                        <select 
                                            required 
                                            value={testForm.template_id} 
                                            onChange={e => setTestForm({...testForm, template_id: e.target.value})} 
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold outline-none focus:border-blue-500"
                                        >
                                            <option value="">-- Выберите тест из библиотеки --</option>
                                            {templates.map(t => (
                                                <option key={t.id} value={t.id}>{t.title} ({t.question_count || t.questions?.length || 0} вопр.)</option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                <div className="flex gap-3">
                                    <div className="flex-1">
                                        <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Начало</label>
                                        <input type="datetime-local" required value={testForm.start_time} onChange={e => setTestForm({...testForm, start_time: e.target.value})} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold outline-none" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Дедлайн</label>
                                        <input type="datetime-local" required value={testForm.end_time} onChange={e => setTestForm({...testForm, end_time: e.target.value})} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold outline-none" />
                                    </div>
                                </div>

                                <div className="border-t border-gray-100 pt-3">
                                    <label className="text-[10px] font-black text-gray-400 uppercase block mb-2">Кому назначить</label>
                                    <select value={testForm.assign_type} onChange={e => setTestForm({...testForm, assign_type: e.target.value, selected_students: []})} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold outline-none focus:border-blue-500 mb-2">
                                        <option value="group">Всей группе (Всем ученикам)</option>
                                        <option value="individual">Индивидуально (Выбрать учеников)</option>
                                    </select>

                                    {testForm.assign_type === 'individual' && (
                                        <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-xl p-3 bg-white space-y-2">
                                            {students.length === 0 && <p className="text-xs text-gray-400">В группе нет учеников</p>}
                                            {students.map(s => (
                                                <label key={s.id} className="flex items-center gap-3 text-sm font-bold text-gray-700 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-4 h-4 text-blue-600 rounded" 
                                                        checked={testForm.selected_students.includes(s.id)} 
                                                        onChange={(e) => {
                                                            const newSel = e.target.checked ? [...testForm.selected_students, s.id] : testForm.selected_students.filter(id => id !== s.id);
                                                            setTestForm({...testForm, selected_students: newSel});
                                                        }} 
                                                    />
                                                    {s.name}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button type="submit" disabled={templates.length === 0} className="w-full bg-blue-600 disabled:bg-blue-300 hover:bg-blue-700 text-white font-black py-3 rounded-xl transition-colors shadow-sm">
                                Опубликовать тест
                            </button>
                        </form>
                    </div>
                )}

                {/* Аналитика */}
                {isAnalyticsModalOpen && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-3xl p-6 w-full max-w-2xl shadow-2xl relative max-h-[85vh] flex flex-col">
                            <button onClick={() => setIsAnalyticsModalOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-800 font-bold text-lg">✕</button>
                            
                            <div className="mb-6">
                                <h2 className="text-lg font-black text-gray-800">📊 Результаты тестирования</h2>
                                <p className="text-xs text-gray-500 font-bold mt-1 uppercase tracking-widest">{currentTestName}</p>
                            </div>

                            <div className="overflow-y-auto flex-1 border border-gray-200 rounded-xl">
                                {analyticsLoading ? (
                                    <div className="p-12 text-center text-sm font-bold text-gray-400">Загрузка результатов...</div>
                                ) : analyticsData.length === 0 ? (
                                    <div className="p-12 text-center text-sm font-bold text-gray-400">Нет данных для отображения</div>
                                ) : (
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-gray-50 border-b border-gray-200 text-xs font-black text-gray-500 uppercase tracking-widest sticky top-0">
                                            <tr>
                                                <th className="px-4 py-3">Ученик</th>
                                                <th className="px-4 py-3 text-center">Статус</th>
                                                <th className="px-4 py-3 text-center">Баллы</th>
                                                <th className="px-4 py-3 text-right">Успеваемость</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {analyticsData.map((row) => (
                                                <tr key={row.student_id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 font-bold text-gray-800">{row.student_name}</td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest ${row.status === 'Сдал' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                                            {row.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center font-black text-gray-600">
                                                        {row.status === 'Сдал' ? `${row.score} / ${row.total_questions}` : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        {row.status === 'Сдал' ? (
                                                            <span className={`font-black ${row.percentage >= 80 ? 'text-green-600' : row.percentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                                                                {row.percentage}%
                                                            </span>
                                                        ) : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Предпросмотр вопросов назначенного теста */}
                {isQuestionsPreviewOpen && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-3xl p-6 w-full max-w-3xl shadow-2xl relative max-h-[85vh] flex flex-col">
                            <button onClick={() => setIsQuestionsPreviewOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-800 font-bold text-lg z-10">✕</button>
                            
                            <div className="mb-4 pb-4 border-b border-gray-100">
                                <h2 className="text-lg font-black text-gray-800">👁️ Содержимое теста</h2>
                                <p className="text-xs text-gray-500 font-bold mt-1 uppercase tracking-widest">{currentTestName}</p>
                            </div>

                            <div className="overflow-y-auto flex-1 space-y-4 pr-2 custom-scrollbar">
                                {previewLoading ? (
                                    <div className="p-12 text-center text-sm font-bold text-gray-400">Загрузка вопросов...</div>
                                ) : previewQuestions.length === 0 ? (
                                    <div className="p-12 text-center text-sm font-bold text-gray-400">В этом тесте нет вопросов.</div>
                                ) : (
                                    previewQuestions.map((q, i) => (
                                        <div key={q.id || i} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                            <div className="font-bold text-sm text-gray-800 mb-3 flex gap-2">
                                                <span className="text-blue-600">{i + 1}.</span> 
                                                <MathText text={q.text || 'Текст вопроса недоступен'} />
                                            </div>
                                            {q.image && <img src={q.image} alt="Вопрос" className="max-h-32 object-contain mb-3 rounded-lg border" />}
                                            <div className="space-y-1 pl-6">
                                                {q.answers?.map((ans, idx) => (
                                                    <div key={idx} className={`text-xs font-semibold flex gap-2 ${ans.is_correct ? 'text-green-600 font-black' : 'text-gray-600'}`}>
                                                        <span>{['А', 'Б', 'В', 'Г', 'Д'][idx] + ')'}</span>
                                                        <MathText text={ans.text} />
                                                        {ans.is_correct && ' ✅'}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Создание группы */}
                {isCreateGroupModalOpen && (
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                        <form onSubmit={handleCreateGroup} className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl relative">
                            <button type="button" onClick={closeCreateGroupModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-800 font-bold text-lg">✕</button>
                            <h2 className="text-lg font-black text-gray-800 mb-4">Создать новую группу</h2>
                            <input type="text" placeholder="Название группы (например: 9Б класс)" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-blue-500 mb-4 text-sm font-semibold" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} autoFocus />
                            <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-3 rounded-xl transition-colors">Создать</button>
                        </form>
                    </div>
                )}
                
                {/* Приглашение ученика */}
                {isInviteModalOpen && (
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                        <form onSubmit={handleInviteStudent} className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl relative overflow-visible">
                            <button type="button" onClick={() => { setIsInviteModalOpen(false); setInviteUsername(''); setSearchResults([]); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-800 font-bold text-lg">✕</button>
                            <h2 className="text-lg font-black text-gray-800 mb-4">Пригласить ученика</h2>
                            
                            <div className="relative w-full mb-6">
                                <input 
                                    type="text" 
                                    placeholder="Введите логин или имя (минимум 2 буквы)..." 
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white text-sm font-semibold transition-colors" 
                                    value={inviteUsername} 
                                    onChange={(e) => setInviteUsername(e.target.value)} 
                                    autoFocus 
                                />
                                
                                {isSearching && (
                                    <div className="absolute right-4 top-3.5 text-blue-500 text-xs font-black animate-pulse">Ищем...</div>
                                )}

                                {searchResults.length > 0 && (
                                    <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-xl mt-2 max-h-48 overflow-y-auto divide-y divide-gray-50">
                                        {searchResults.map((user) => (
                                            <li
                                                key={user.id}
                                                className="px-4 py-3 hover:bg-blue-50 cursor-pointer flex justify-between items-center transition-colors"
                                                onClick={() => {
                                                    setInviteUsername(user.username);
                                                    setSearchResults([]);
                                                }}
                                            >
                                                <span className="font-bold text-sm text-gray-900">{user.username}</span>
                                                <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded-md">
                                                    {user.first_name} {user.last_name}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <button type="submit" disabled={!inviteUsername.trim()} className="w-full bg-blue-600 disabled:bg-blue-300 hover:bg-blue-700 text-white font-black py-3 rounded-xl transition-colors shadow-sm">
                                Добавить в группу
                            </button>
                        </form>
                    </div>
                )}
            </>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center min-h-screen items-center">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    // === ГЛАВНЫЙ ВОЗВРАТ КОМПОНЕНТА ===
    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 font-sans relative">
            <div className="max-w-6xl mx-auto space-y-6">
                {leftTab === 'classes' ? renderClassesTab() : renderTemplatesTab()}
            </div>
            {renderModals()}
        </div>
    );
};

export default TeacherDashboard;